import React, { useMemo, useRef, useState } from "react";
import { Window } from "@progress/kendo-react-dialogs";
import { Input } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";
import { Popup } from "@progress/kendo-react-popup";

function SaveQueryPopup({ queryOptions, saveError, onClose, onConfirm }) {
    const scopeOptions = useMemo(() => ["Private Query", "Global Query"], []);

    const [queryName, setQueryName] = useState("");
    const [queryScope, setQueryScope] = useState(scopeOptions[0]);
    const [showError, setShowError] = useState(false);
    const [showAccessPopup, setShowAccessPopup] = useState(false);
    const accessIconRef = useRef(null);
    const hoverTimer = useRef(null);

    function isEmptyOrWhitespace(str) {
        return !str || str.trim().length === 0;
    }

    function handleConfirm() {
        if (isEmptyOrWhitespace(queryName)) {
            setShowError(true);
            return;
        }

        setShowError(false);

        onConfirm({
            queryName: queryName.trim(),
            queryScope,
            queryOptions,
        });
    }

    return (
        <Window
            className="save-query-window"
            title=""
            onClose={onClose}
            height={(showError || saveError) ? 295 : 260}
            initialWidth={520}
            modal={true}
            resizable={false}
        >
            <div className="save-query-header">
                <div className="save-query-header-title">Save Query As</div>

                <Button
                    type="button"
                    className="save-query-close-btn"
                    fillMode="flat"
                    onClick={onClose}
                    aria-label="Close"
                >
                    ×
                </Button>
            </div>

            <div className="save-query-body">
                <div className="save-query-section">
                    <div className="save-query-label">Enter Name for Query</div>

                    <Input
                        value={queryName}
                        onChange={(e) => setQueryName(e.target.value)}
                        placeholder="My query name..."
                    />

                    {showError ? (
                        <div className="save-query-error">
                            Please enter a name for the query
                        </div>
                    ) : saveError ? (
                        <div className="save-query-error">{saveError}</div>
                    ) : null}
                </div>

                <div className="save-query-section">
                    <div className="save-query-label-row">
                        <div className="save-query-label">Select Access</div>

                        <span
                            ref={accessIconRef}
                            className="save-query-tooltip-icon"
                            tabIndex={0}
                            role="button"
                            aria-label="Access info"
                            onMouseEnter={() => {
                                hoverTimer.current = setTimeout(() => {
                                    setShowAccessPopup(true);
                                }, 200);
                            }}
                            onMouseLeave={() => {
                                clearTimeout(hoverTimer.current);
                                setShowAccessPopup(false);
                            }}
                        >
                            ?
                        </span>

                        {showAccessPopup && accessIconRef.current && (
                            <Popup
                                anchor={accessIconRef.current}
                                show={showAccessPopup}
                                popupClass="save-query-tooltip-popup"
                                animate={false}
                                anchorAlign={{ horizontal: "right", vertical: "center" }}
                                popupAlign={{ horizontal: "left", vertical: "center" }}
                            >
                                <div className="save-query-tooltip-popup-content">
                                    <div>
                                        <strong>Private Query</strong> - Visible only to you.
                                    </div>
                                    <div style={{ marginTop: "4px" }}>
                                        <strong>Global Query</strong> - Visible to all project members.
                                    </div>
                                </div>
                            </Popup>
                        )}
                    </div>

                    <DropDownList
                        data={scopeOptions}
                        value={queryScope}
                        onChange={(e) => setQueryScope(e.target.value)}
                    />
                </div>

                <div className="save-query-actions">
                    <Button className="close-query-btn" type="button" onClick={onClose}>
                        Close
                    </Button>

                    <Button
                        className="save-query-submit-btn"
                        type="button"
                        onClick={handleConfirm}
                    >
                        Save Query
                    </Button>
                </div>
            </div>
        </Window>
    );
}

export default SaveQueryPopup;